"use client"

import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table"
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Search,
} from "lucide-react"
import { observer } from "mobx-react-lite"
import { useState } from "react"
import { Line, LineChart } from "recharts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { type ChartConfig, ChartContainer } from "@/components/ui/chart"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface DataTableFilter {
  id: string
  label: string
  value: string
  selected: boolean
}

interface DataTableProps<TData> {
  title?: string
  columns: ColumnDef<TData>[]
  data: TData[]
  filters?: DataTableFilter[]
  onFilterChange?: (filterId: string) => void
  theme?: ThemeConfigType
  className?: string
  enableSearch?: boolean
  searchPlaceholder?: string
  enableCsvExport?: boolean
  csvFilename?: string
  // Initial sorting state (e.g., [{ id: 'date', desc: true }])
  initialSorting?: SortingState
  // i18n translations (optional, defaults to English)
  translations?: {
    filtersButton?: string
    filterBy?: string
    noResults?: string
    rowsPerPage?: string
    page?: string
    of?: string
    totalRows?: string
  }
}

export const DataTable = observer(
  <TData,>({
    title,
    columns,
    data,
    filters = [],
    onFilterChange,
    theme,
    className,
    enableSearch = false,
    searchPlaceholder = "Search...",
    enableCsvExport = false,
    csvFilename = "data.csv",
    initialSorting = [],
    translations = {},
  }: DataTableProps<TData>) => {
    const [sorting, setSorting] = useState<SortingState>(initialSorting)
    const [showFilters, setShowFilters] = useState(false)
    const [globalFilter, setGlobalFilter] = useState("")
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    })

    const activeTheme = theme || getDefaultTheme()
    const themeClasses = useTheme(activeTheme)
    const isDefaultTheme = !theme

    // Destructure translations with English defaults
    const {
      filtersButton = "Filters",
      filterBy = "Filter by:",
      noResults = "No results.",
      rowsPerPage = "Rows per page:",
      page = "Page",
      of = "of",
      totalRows = "total rows",
    } = translations

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onSortingChange: setSorting,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      state: {
        sorting,
        globalFilter,
        pagination,
      },
    })

    const handleFilterToggle = (filterId: string) => {
      onFilterChange?.(filterId)
    }

    const selectedFilters = filters.filter((f) => f.selected)

    // CSV export function
    const exportToCsv = () => {
      const headers = columns
        .map((col) => {
          if (typeof col.header === "string") {
            return col.header
          }
          return ""
        })
        .filter(Boolean)

      const csvData = data.map((row) => {
        return columns
          .map((col) => {
            // biome-ignore lint/suspicious/noExplicitAny: Need to access dynamic properties
            const colDef = col as any
            const accessorKey = colDef.accessorKey || colDef.id
            if (!accessorKey) return ""

            // biome-ignore lint/suspicious/noExplicitAny: Need to access dynamic properties
            const value = (row as any)[accessorKey]
            if (value === null || value === undefined) return ""
            // Escape quotes and wrap in quotes if contains comma
            const stringValue = String(value)
            if (stringValue.includes(",") || stringValue.includes('"')) {
              return `"${stringValue.replace(/"/g, '""')}"`
            }
            return stringValue
          })
          .join(",")
      })

      const csv = [headers.join(","), ...csvData].join("\n")
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
      const link = document.createElement("a")
      const url = URL.createObjectURL(blob)
      link.setAttribute("href", url)
      link.setAttribute("download", csvFilename)
      link.style.visibility = "hidden"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    return (
      <Card
        className={cn(
          "w-full",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
      >
        {(title || filters.length > 0 || enableSearch || enableCsvExport) && (
          <CardHeader>
            <div className="flex items-center justify-between mb-4">
              {title && (
                <CardTitle className={cn(!isDefaultTheme && themeClasses.typography)}>
                  {title}
                </CardTitle>
              )}
              <div className="flex items-center gap-2">
                {filters.length > 0 && (
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {filtersButton}
                      {selectedFilters.length > 0 && (
                        <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                          {selectedFilters.length}
                        </span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                    {showFilters && (
                      <div className="absolute right-0 top-full mt-2 w-64 rounded-md border bg-popover p-4 shadow-lg z-50">
                        <div className="space-y-2">
                          <p className="text-sm font-medium mb-3">{filterBy}</p>
                          {filters.map((filter) => (
                            <div key={filter.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={filter.id}
                                checked={filter.selected}
                                onCheckedChange={() => handleFilterToggle(filter.id)}
                              />
                              <label
                                htmlFor={filter.id}
                                className="text-sm font-normal cursor-pointer flex-1"
                              >
                                {filter.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {enableCsvExport && (
                  <Button variant="outline" size="sm" onClick={exportToCsv}>
                    <Download className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            {enableSearch && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={searchPlaceholder}
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </CardHeader>
        )}
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : (
                          // biome-ignore lint/a11y/noStaticElementInteractions: Using div with role=button is intentional for table header
                          <div
                            className={cn(
                              "flex items-center gap-2",
                              header.column.getCanSort() && "cursor-pointer select-none",
                            )}
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                header.column.getToggleSortingHandler()?.(e)
                              }
                            }}
                            role={header.column.getCanSort() ? "button" : undefined}
                            tabIndex={header.column.getCanSort() ? 0 : undefined}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getCanSort() &&
                              (header.column.getIsSorted() === "asc" ? (
                                <ArrowUp className="h-4 w-4" />
                              ) : header.column.getIsSorted() === "desc" ? (
                                <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 opacity-50" />
                              ))}
                          </div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      {noResults}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {/* Pagination Controls */}
          {table.getPageCount() > 1 && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">{rowsPerPage}</p>
                <Select
                  value={`${table.getState().pagination.pageSize}`}
                  onValueChange={(value) => {
                    table.setPageSize(Number(value))
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue placeholder={table.getState().pagination.pageSize} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[10, 20, 50, 100].map((pageSize) => (
                      <SelectItem key={pageSize} value={`${pageSize}`}>
                        {pageSize}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    {page} {table.getState().pagination.pageIndex + 1} {of} {table.getPageCount()}
                  </span>
                  <span className="hidden sm:inline">
                    ({table.getFilteredRowModel().rows.length} {totalRows})
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    )
  },
)

DataTable.displayName = "DataTable"

// Helper function to create sortable columns
export function createSortableColumn<TData>(accessorKey: string, header: string): ColumnDef<TData> {
  return {
    accessorKey,
    header,
    enableSorting: true,
  }
}

// Helper renderer for trend badges
export function renderTrendBadge(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return (
      <Badge variant="outline" className="bg-muted text-muted-foreground">
        N/A
      </Badge>
    )
  }

  const isPositive = value > 0
  const isNegative = value < 0

  return (
    <Badge
      variant={isPositive ? "default" : "outline"}
      className={cn(
        isPositive && "bg-green-500 text-white hover:bg-green-600",
        isNegative && "bg-orange-500 text-white hover:bg-orange-600",
      )}
    >
      {isPositive ? "+" : ""}
      {value.toFixed(1)}%
    </Badge>
  )
}

// Helper renderer for trend line chart
export function renderTrendChart(data: Array<{ value: number }>) {
  const chartConfig = {
    value: {
      label: "Trend",
      color: "hsl(var(--primary))",
    },
  } satisfies ChartConfig

  return (
    <ChartContainer config={chartConfig} className="h-12 w-24">
      <LineChart data={data} accessibilityLayer>
        <Line
          type="monotone"
          dataKey="value"
          stroke="var(--color-value)"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ChartContainer>
  )
}

// Helper renderer for percentage values
export function renderPercent(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return <span className="text-muted-foreground">N/A</span>
  }

  return (
    <span>
      <span className="text-base font-normal">{value.toFixed(1)}</span>
      <span className="text-xs text-muted-foreground ml-0.5">%</span>
    </span>
  )
}
